import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { SERVICES } from '../constants/services';
import SectionLink from './SectionLink';

// Per-card accent wash, cycled by position — gives each service a distinct
// identity while staying inside the site's indigo/sky/violet palette family.
const SERVICE_ACCENTS = [
  { glow: 'rgba(99,102,241,0.55)', bar: '#818cf8' }, // indigo
  { glow: 'rgba(56,189,248,0.5)', bar: '#38bdf8' }, // sky
  { glow: 'rgba(168,85,247,0.45)', bar: '#c084fc' }, // violet
  { glow: 'rgba(52,211,153,0.4)', bar: '#34d399' }, // emerald
  { glow: 'rgba(251,113,133,0.4)', bar: '#fb7185' }, // rose
  { glow: 'rgba(250,204,21,0.4)', bar: '#facc15' }, // amber
];

function ServicePanel({ service, index }) {
  const reduceMotion = useReducedMotion();
  const ref = useRef(null);
  const accent = SERVICE_ACCENTS[index % SERVICE_ACCENTS.length];

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'start 0.35'],
  });

  const y = useTransform(scrollYProgress, [0, 1], reduceMotion ? [0, 0] : [48, 0]);
  const opacity = useTransform(scrollYProgress, [0, 0.45, 1], reduceMotion ? [1, 1, 1] : [0.25, 1, 1]);

  return (
    <article
      ref={ref}
      className="service-stack-card"
      style={{ zIndex: index + 1 }}
      data-service-id={service.id}
      aria-labelledby={`service-title-${service.id}`}
    >
      <motion.div
        className="service-slide"
        style={reduceMotion ? undefined : { y, opacity }}
      >
        <img
          src={service.image}
          alt=""
          aria-hidden="true"
          loading="lazy"
          className="service-slide-bg"
        />
        <div className="service-slide-overlay" aria-hidden="true" />
        <div
          className="service-accent-glow"
          aria-hidden="true"
          style={{ backgroundColor: accent.glow }}
        />

        <div className="service-slide-body">
          <h3 id={`service-title-${service.id}`} className="service-slide-title">
            {service.title}
          </h3>
          <p className="service-slide-summary">{service.summary}</p>

          <ul className="service-panel-list" aria-label={`${service.title} deliverables`}>
            {service.list.map((item) => (
              <li key={item} className="service-panel-item">
                <span
                  className="service-panel-dot"
                  aria-hidden="true"
                  style={{ backgroundColor: accent.bar }}
                />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </motion.div>
    </article>
  );
}

function CardList() {
  return (
    <>
      <div className="service-stack" aria-label="All services">
        {SERVICES.map((service, index) => (
          <ServicePanel key={service.id} service={service} index={index} />
        ))}
        {/* Runway so the last card can exit the sticky stack smoothly */}
        <div className="service-stack-end-spacer" aria-hidden="true" />
      </div>

      <div className="mt-10 flex justify-center lg:hidden">
        <SectionLink sectionId="contactus" className="service-cta-button">
          Discuss Your Project
          <span aria-hidden="true">↗</span>
        </SectionLink>
      </div>
    </>
  );
}

export default CardList;
